import type { SvgIconComponent } from '@mui/icons-material';

import {
  type InputAdornmentProps,
  InputAdornment as MuiInputAdornment,
  TextField as MuiTextField,
  type SxProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { legacyColors } from '@ovComponents/legacyColor';
import {
  formatPhoneNumberForInput,
  getLocaleNumberSeparators,
} from '@utils/common';
import { dollars2cents } from '@utils/dollars2cents';
import { isCurrencySymbolAfter } from '@utils/moneyFormatting';
import { Box, IconButton, Tooltip, Typography, useTheme } from 'hephaestus/web';
import {
  AttachMoneyRounded as AttachMoneyRoundedIcon,
  CalendarMonth as CalendarMonthIcon,
  Lock as LockIcon,
  PercentRounded as PercentRoundedIcon,
  Search as SearchIcon,
  Translate as TranslateIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from 'hephaestus/webIcons';
import {
  type CSSProperties,
  type FocusEventHandler,
  forwardRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  NumberFormatBase,
  NumericFormat,
  PatternFormat,
} from 'react-number-format';
import { FieldLabel, useFieldIds } from '../fieldLabel/fieldLabel';
import { HelpText } from '../helpText/helpText';
import { useHelpTextTokens } from '../helpText/helpText.tokens';
import { useTextFieldTokens } from './textField.tokens';

export const InputAdornment = (props: InputAdornmentProps) => (
  <MuiInputAdornment {...props} />
);

type FormatterProps = {
  onChange: (event: {
    target: { valueCents: undefined | number; name: string; value: string };
  }) => void;
  name: string;
  decimalplaces: number;
  unlimitedDecimals?: boolean;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
};

interface CurrencyFormatterProps extends FormatterProps {
  value?: string | number | null;
}

export type TextFieldProps = {
  id?: string;
  name?: string;
  testId?: string;
  'data-testid'?: string;
  type?:
    | 'default'
    | 'multiLanguage'
    | 'number'
    | 'dollar'
    | 'phone'
    | '3-3-3'
    | '3-2-4'
    | '2-7'
    | 'password';
  label?: string;
  placeholder?: string;
  helpText?: string;
  error?: boolean;
  errorText?: string;
  success?: boolean;
  successText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  locked?: boolean;
  lockMessage?: string;
  sx?: SxProps;
  leadingIcon?: 'dollar' | 'percent' | 'search';
  trailingIcon?: 'calendar' | 'percent' | 'dollar';
  size?: 'small' | 'medium';
  textSize?: string | number;
  textAlign?: 'left' | 'center' | 'right';
  children?: React.ReactNode[] | React.ReactNode;
  select?: boolean;
  value?: any;
  defaultValue?: any;
  onChange?: any;
  onFocus?: any;
  onBlur?: any;
  onClick?: any;
  onPaste?: any;
  onTranslate?: any;
  renderValue?: any;
  InputProps?: any;
  inputProps?: any;
  multiline?: boolean;
  minRows?: number | string;
  maxRows?: number | string;
  rows?: number | string;
  infoTooltip?: string;
  multiple?: boolean;
  required?: boolean;
  onKeyDown?: any;
  menuTestId?: string;
  decimalPlaces?: number;
  unlimitedDecimals?: boolean;
  visibilityToggle?: boolean;
  onVisibilityChange?: (reveal: boolean) => void;
  showArrows?: boolean;
  bottomLabel?: string;
  autocomplete?: 'on' | 'off';
  SelectProps?: any;
};

const StyledTextField = styled(MuiTextField)(() => {
  const theme = useTheme();
  const tokens = useTextFieldTokens(theme);

  return {
    '& .MuiOutlinedInput-root': {
      '& input': {
        paddingLeft: '12px',
        paddingRight: '12px',
        color: tokens.textColor,
        textAlign: 'inherit',
        '&::placeholder': {
          textAlign: 'inherit',
        },
        '&:disabled': {
          color: tokens.disabledColor,
        },
      },
      '&.hide-number-spinners input[type="number"]': {
        MozAppearance: 'textfield',
        '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
      },
      '&.Mui-focused fieldset': {
        borderColor: tokens.focusBorderColor,
      },
      '& fieldset': {
        borderColor: tokens.borderColor,
      },
      '&.MuiInputBase-adornedStart .MuiInputAdornment-positionStart': {
        marginRight: '0px',
      },
      '& .MuiSelect-select': {
        paddingLeft: '0px',
        marginLeft: '12px',
        marginRight: '12px',
        color: tokens.textColor,
      },
      '& .MuiInputAdornment-positionEnd': {
        marginLeft: '0px',
      },
      '&.Mui-error': {
        '& input': {
          color: tokens.errorColor,
        },
        '& fieldset': {
          borderColor: tokens.errorColor,
        },
      },
    },
  };
});

const CurrencyFormatter = forwardRef<HTMLInputElement, CurrencyFormatterProps>(
  (props, ref) => {
    const {
      onChange,
      decimalplaces = 2,
      unlimitedDecimals = false,
      onFocus: forwardedOnFocus,
      onBlur: forwardedOnBlur,
      value: rawValue,
      ...other
    } = props;

    // Pad displayed value with `fixedDecimalScale={false}` — focused
    // NumberFormat skips prop updates whose float matches its state, so typed
    // `35.4` stays on screen while we feed `"35.40"`. `setIsFocused`'s blur
    // re-render is what lets it finally repaint the padded form; don't gate
    // display logic on `isFocused` (NumberFormat's `setTimeout(0)` lags state).
    const [, setIsFocused] = useState(false);
    const { thousandSeparator, decimalSeparator } = getLocaleNumberSeparators();

    const displayValue = ((): string | number | null | undefined => {
      if (rawValue === '' || rawValue === undefined || rawValue === null) {
        return rawValue;
      }
      // Strip the current locale's grouping separators and rewrite its decimal
      // separator so `Number()` can interpret strings that callers have already
      // run through `Intl.NumberFormat` (e.g. `formatDecimalNumber` =>
      // `"12,345.00"`). Without this the toFixed pad below silently blanks the
      // input on initial render (e.g. ledger Update Transaction dialog).
      let normalizedValue: string | number = rawValue;
      if (typeof rawValue === 'string') {
        let stripped = rawValue;
        if (thousandSeparator) {
          stripped = stripped.split(thousandSeparator).join('');
        }
        if (decimalSeparator && decimalSeparator !== '.') {
          stripped = stripped.split(decimalSeparator).join('.');
        }
        normalizedValue = stripped;
      }
      const numericValue = Number(normalizedValue);
      if (!Number.isFinite(numericValue)) return '';
      if (unlimitedDecimals) {
        return typeof normalizedValue === 'string'
          ? normalizedValue
          : String(normalizedValue);
      }
      return numericValue.toFixed(decimalplaces);
    })();

    return (
      <NumericFormat
        {...other}
        value={displayValue}
        getInputRef={ref}
        onValueChange={(values, sourceInfo) => {
          // !todo: onValueChange is triggered on any event or prop change - don't need that if it's not user generated,
          // it forces react to re-trigger state change and rebuild the portfolio table multiple times
          /** @see https://s-yadav.github.io/react-number-format/docs/props#onvaluechange-values-sourceinfo-- */
          if (sourceInfo.source !== 'event') return;
          const { value } = values;

          if (value !== '' && Number.isNaN(Number(value))) {
            return;
          }

          onChange({
            target: {
              name: props.name,
              value,
              valueCents:
                value === ''
                  ? undefined
                  : dollars2cents(value, decimalplaces, unlimitedDecimals),
            },
          });
        }}
        onFocus={(e) => {
          setIsFocused(true);
          forwardedOnFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          forwardedOnBlur?.(e);
        }}
        {...(unlimitedDecimals ? {} : { decimalScale: decimalplaces })}
        fixedDecimalScale={false}
        thousandSeparator={thousandSeparator}
        decimalSeparator={decimalSeparator}
        valueIsNumericString
        defaultValue={0}
        allowNegative={true}
        allowLeadingZeros={false}
      />
    );
  },
);

export const DigitFormatter333 = forwardRef<HTMLInputElement, FormatterProps>(
  (props, ref) => {
    const { onChange, ...other } = props;

    return (
      <PatternFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values, sourceInfo) => {
          if (sourceInfo.source !== 'event') return;
          onChange({
            target: {
              name: props.name,
              value: values.value,
              valueCents: undefined,
            },
          });
        }}
        format={'### ### ###'}
        valueIsNumericString
      />
    );
  },
);

export const DigitFormatter324 = forwardRef<HTMLInputElement, FormatterProps>(
  (props, ref) => {
    const { onChange, ...other } = props;

    return (
      <PatternFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values, sourceInfo) => {
          if (sourceInfo.source !== 'event') return;
          onChange({
            target: {
              name: props.name,
              value: values.value,
              valueCents: undefined,
            },
          });
        }}
        format={'###-##-####'}
        valueIsNumericString
      />
    );
  },
);

export const DigitFormatter27 = forwardRef<HTMLInputElement, FormatterProps>(
  (props, ref) => {
    const { onChange, ...other } = props;

    return (
      <PatternFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values, sourceInfo) => {
          if (sourceInfo.source !== 'event') return;
          onChange({
            target: {
              name: props.name,
              value: values.value,
              valueCents: undefined,
            },
          });
        }}
        format={'##-#######'}
        valueIsNumericString
      />
    );
  },
);

export const PhoneFormatter = forwardRef<HTMLInputElement, FormatterProps>(
  (props, ref) => {
    const { onChange, ...other } = props;

    return (
      <NumberFormatBase
        {...other}
        getInputRef={ref}
        onValueChange={(values, sourceInfo) => {
          if (sourceInfo.source !== 'event') return;
          onChange({
            target: {
              name: props.name,
              value: values.value ? `+${values.value}` : '',
              valueCents: undefined,
            },
          });
        }}
        format={formatPhoneNumberForInput}
        removeFormatting={(value) => value.replace(/\D/g, '')}
        valueIsNumericString
      />
    );
  },
);

export const TextField = ({
  id,
  name,
  label,
  type = 'default',
  helpText,
  error = false,
  errorText,
  success = false,
  successText,
  fullWidth,
  disabled = false,
  locked = false,
  lockMessage,
  sx,
  leadingIcon,
  trailingIcon,
  size = 'medium',
  onChange,
  onFocus,
  onBlur,
  onClick,
  onTranslate,
  renderValue,
  placeholder,
  children,
  select,
  value,
  infoTooltip,
  multiple,
  required,
  onKeyDown,
  testId,
  'data-testid': dataTestId,
  decimalPlaces = 2,
  unlimitedDecimals = false,
  visibilityToggle,
  onVisibilityChange,
  showArrows = true,
  textSize = '16px',
  textAlign = 'left',
  bottomLabel,
  autocomplete,
  menuTestId,
  ...restProps
}: TextFieldProps) => {
  const theme = useTheme();
  const tokens = useTextFieldTokens(theme);
  const helpTextTokens = useHelpTextTokens(theme);
  const { t } = useTranslation('components');
  const [visibility, setVisibility] = useState<boolean>(false);
  const resolvedTestId = dataTestId ?? testId ?? 'text-field';
  const { inputId, labelId } = useFieldIds(id);
  const labelledBy = label ? labelId : undefined;
  const variantStyle: CSSProperties = {};

  const shouldDisplayInDialog = helpTextTokens.displayInDialog;
  const helpTextToShow =
    infoTooltip && !shouldDisplayInDialog ? infoTooltip : helpText;

  let effectiveLeadingIcon = leadingIcon;
  let effectiveTrailingIcon = trailingIcon;

  if (type === 'dollar') {
    const symbolAfter = isCurrencySymbolAfter();
    const hasLeadingDollar = leadingIcon === 'dollar';
    const hasTrailingDollar = trailingIcon === 'dollar';

    if (!hasLeadingDollar && !hasTrailingDollar) {
      effectiveLeadingIcon = symbolAfter ? undefined : 'dollar';
      effectiveTrailingIcon = symbolAfter ? 'dollar' : undefined;
    } else if (hasLeadingDollar && !hasTrailingDollar && symbolAfter) {
      // Backward-compatible migration path: legacy leading dollar icon should trail in non-English locales.
      effectiveLeadingIcon = undefined;
      effectiveTrailingIcon = 'dollar';
    }
  }

  let leadingIconComponent: SvgIconComponent | null = null;
  let trailingIconComponent: SvgIconComponent | null = null;

  switch (effectiveLeadingIcon) {
    case 'dollar':
      leadingIconComponent = AttachMoneyRoundedIcon;
      break;
    case 'percent':
      leadingIconComponent = PercentRoundedIcon;
      break;
    case 'search':
      leadingIconComponent = SearchIcon;
      break;
  }
  switch (effectiveTrailingIcon) {
    case 'calendar':
      trailingIconComponent = CalendarMonthIcon;
      break;
    case 'percent':
      trailingIconComponent = PercentRoundedIcon;
      break;
    case 'dollar':
      trailingIconComponent = AttachMoneyRoundedIcon;
      break;
  }

  const style = {
    ...variantStyle,
    height: restProps.multiline ? 'auto' : size === 'medium' ? '40px' : '32px',
    fontSize: textSize,
    fontFamily: theme.font.base,
    borderRadius: '4px',
    width: fullWidth ? '100%' : 'auto',
    background: legacyColors.white,
  };

  const footerText = error
    ? (errorText ?? t('fieldErrors.default', { fieldName: label }))
    : success
      ? successText
      : helpTextToShow;

  const getFormatter = (): any => {
    switch (type) {
      case 'dollar': {
        return CurrencyFormatter;
      }
      case '3-3-3': {
        return DigitFormatter333;
      }
      case '3-2-4': {
        return DigitFormatter324;
      }
      case '2-7': {
        return DigitFormatter27;
      }
      case 'phone': {
        return PhoneFormatter;
      }
      default: {
        return undefined;
      }
    }
  };

  const htmlInputType =
    type === 'number'
      ? 'number'
      : type === 'phone'
        ? 'tel'
        : type === 'password'
          ? 'password'
          : 'text';

  const endAdornment: JSX.Element[] = [];

  if (locked) {
    endAdornment.push(
      <Tooltip title={lockMessage ?? ''} key='lock-icon'>
        <Icon icon={LockIcon} size='small' />
      </Tooltip>,
    );
  }
  const iconButtonSize =
    style.height === 'auto'
      ? 'auto'
      : `${Number(style.height.slice(0, -2)) - 8}px`;

  if (type === 'multiLanguage') {
    endAdornment.push(
      <IconButton
        aria-label='translate'
        key='translate-icon'
        style={{
          height: iconButtonSize,
          width: iconButtonSize,
        }}
        onClick={locked || disabled ? null : onTranslate}
      >
        <Icon icon={TranslateIcon} size='small' />
      </IconButton>,
    );
  }

  if (visibilityToggle) {
    endAdornment.push(
      <IconButton
        key='visibility-icon'
        aria-label={visibility ? 'hide' : 'show'}
        style={{
          height: iconButtonSize,
          width: iconButtonSize,
        }}
        onClick={(e) => {
          setVisibility(!visibility);
          onVisibilityChange?.(!visibility);
          e.stopPropagation();
        }}
      >
        <Icon
          icon={visibility ? VisibilityIcon : VisibilityOffIcon}
          size='small'
        />
      </IconButton>,
    );
  }

  if (trailingIconComponent) {
    endAdornment.push(
      <Icon icon={trailingIconComponent} size='small' key='trailing-icon' />,
    );
  }

  return (
    <Box sx={sx as any}>
      <FieldLabel
        htmlFor={label && !select ? inputId : undefined}
        id={labelledBy}
        error={error}
        required={required}
        infoTooltip={infoTooltip}
        shouldDisplayInDialog={shouldDisplayInDialog}
        data-testid={`${resolvedTestId}-label`}
      >
        {label}
      </FieldLabel>
      <StyledTextField
        {...restProps}
        id={inputId}
        name={name}
        data-testid={resolvedTestId}
        type={htmlInputType}
        onClick={onClick}
        onChange={onChange}
        error={!!error}
        style={style}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled || locked}
        placeholder={placeholder}
        select={select}
        value={value}
        required={required}
        autoComplete={autocomplete || 'on'}
        InputProps={{
          style,
          className:
            type === 'number' && !showArrows ? 'hide-number-spinners' : '',
          inputComponent: getFormatter(),
          startAdornment: leadingIconComponent ? (
            <MuiInputAdornment position='start'>
              <Icon icon={leadingIconComponent} size='small' />
            </MuiInputAdornment>
          ) : null,
          endAdornment: endAdornment.length > 0 && (
            <MuiInputAdornment position='end'>
              {' '}
              {endAdornment}{' '}
            </MuiInputAdornment>
          ),
          ...restProps.InputProps,
          inputProps: {
            ...(restProps.inputProps ?? {}),
            ...restProps.InputProps?.inputProps,
            ...(select
              ? { name }
              : {
                  id: inputId,
                  name,
                  'aria-labelledby': labelledBy,
                }),
            'data-testid': `${resolvedTestId}-input`,
            decimalplaces: decimalPlaces,
            unlimitedDecimals,
            step: 'any',
            onInvalid: (e: React.InvalidEvent<HTMLInputElement>) => {
              const message =
                htmlInputType === 'number'
                  ? t('fieldValidation.enterNumber')
                  : t('fieldValidation.required');
              e.target.setCustomValidity(message);
            },
            onInput: (e: React.FormEvent<HTMLInputElement>) => {
              (e.target as HTMLInputElement).setCustomValidity('');
            },
          },
        }}
        onKeyDown={onKeyDown}
        SelectProps={
          select
            ? {
                renderValue,
                ...(locked ? { IconComponent: 'span' } : {}),
                multiple: multiple ?? undefined,
                MenuProps: {
                  PaperProps: {
                    'data-testid': `${menuTestId}`,
                  },
                  ...restProps.SelectProps?.MenuProps,
                },
                ...restProps.SelectProps,
                labelId: labelledBy ?? restProps.SelectProps?.labelId,
              }
            : undefined
        }
      >
        {children}
      </StyledTextField>
      {footerText && (
        <HelpText
          text={footerText}
          tone={error ? 'error' : success ? 'success' : 'neutral'}
          showIcon={
            !(!shouldDisplayInDialog && !error && !success && !!helpTextToShow)
          }
        />
      )}
      {bottomLabel && (
        <Typography
          variant='labelSmall'
          component='div'
          sx={
            {
              mt: '4px',
              color: error ? tokens.errorColor : tokens.labelColor,
            } as any
          }
          data-testid={`${resolvedTestId}-bottom-label`}
        >
          {bottomLabel}
        </Typography>
      )}
    </Box>
  );
};
